import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent } from '@/components/ui';
import { SubscriptionStatusBadge } from './subscription-status-badge';
import { SubscriptionRenewalsTimeline } from './subscription-renewals-timeline';
import { useSubscriptionRenewals } from '@/hooks/use-admin-subscriptions';
import { useService } from '@/hooks/use-services';
import { formatDate, formatCents } from '@/lib/utils';
import {
  findInitialTransactionId,
  formatChargeAmount,
  computeTotalRevenue,
  isPartialHistory,
} from '@/lib/utils/subscription-billing';
import type { AdminSubscription } from '@/types/admin-subscription';

interface SubscriptionDetailCardProps {
  subscription: AdminSubscription;
  onTerminate?: () => void;
  isTerminating: boolean;
}

function formatInterval(interval: string, t: (key: string) => string): string {
  const key = `intervals.${interval}`;
  const translated = t(key);
  return translated === key ? interval : translated;
}

/**
 * Detail-Card (Spec 0222 §5): zeigt nur Felder, die die Facade tatsaechlich
 * liefert, plus Preis/Intervall via Service-Join (B7) und die lazy geladene
 * Abrechnungshistorie. Die Card wird nur bei aufgeklappter Zeile gemountet —
 * beide Queries laufen dadurch lazy (AC2: staleTime verhindert Refetch beim
 * Wieder-Aufklappen).
 */
export function SubscriptionDetailCard({
  subscription: sub,
  onTerminate,
  isTerminating,
}: SubscriptionDetailCardProps) {
  const { t } = useTranslation('subscriptions');

  const renewalsQuery = useSubscriptionRenewals(sub.subscriptionId);
  // Service-Join: gelöschter/fremder Service -> Zeile ausblenden, kein Fehler (§10)
  const serviceQuery = useService(sub.serviceId, sub.merchantId);
  const service = serviceQuery.data;

  // K2: Erst-TX aus dem create-Event der Historie, nicht aus Array-Position
  const initialTransactionId = renewalsQuery.data
    ? findInitialTransactionId(renewalsQuery.data.renewals)
    : undefined;

  // F3: Gesamtumsatz nur bei vollstaendiger Historie mit einheitlicher Waehrung
  const totalRevenue = renewalsQuery.data
    ? computeTotalRevenue(renewalsQuery.data.renewals, {
        partial: isPartialHistory(sub.renewalCount, renewalsQuery.data.renewals),
        truncated: renewalsQuery.data.truncated === true,
      })
    : undefined;

  return (
    <Card className="m-2 border-muted">
      <CardContent className="py-4">
        <div className="grid gap-3 md:grid-cols-2">
          {sub.msisdn && (
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.msisdn')}</p>
              <p className="font-mono text-sm">{sub.msisdn}</p>
            </div>
          )}

          {sub.operatorId && (
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.operator')}</p>
              <p>{sub.operatorId}</p>
            </div>
          )}

          {sub.flowType && (
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.flow')}</p>
              <p>{sub.flowType}</p>
            </div>
          )}

          <div>
            <p className="text-sm text-muted-foreground">{t('detail.status')}</p>
            <SubscriptionStatusBadge status={sub.status} />
          </div>

          {service && !serviceQuery.isError && (
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.servicePrice')}</p>
              <p>
                {formatCents(service.grossAmount)} / {formatInterval(service.subscriptionInterval, t)}
              </p>
            </div>
          )}

          <div>
            <p className="text-sm text-muted-foreground">{t('detail.chargeCount')}</p>
            <p>{sub.renewalCount ?? '—'}</p>
          </div>

          {totalRevenue && (
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.totalRevenue')}</p>
              <p>{formatChargeAmount(totalRevenue.amount, totalRevenue.currency)}</p>
            </div>
          )}

          <div>
            <p className="text-sm text-muted-foreground">{t('detail.created')}</p>
            <p>{formatDate(sub.createdAt)}</p>
          </div>

          {sub.lastRenewalAt && (
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.lastRenewal')}</p>
              <p>{formatDate(sub.lastRenewalAt)}</p>
            </div>
          )}

          {sub.terminatedAt && (
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.terminatedAt')}</p>
              <p>{formatDate(sub.terminatedAt)}</p>
            </div>
          )}

          {initialTransactionId && (
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.initialTransaction')}</p>
              <Link
                to={`/transactions/${initialTransactionId}`}
                className="font-mono text-sm text-primary hover:underline"
              >
                {initialTransactionId} ↗
              </Link>
            </div>
          )}
        </div>

        <div className="mt-4 border-t pt-3">
          <p className="mb-2 text-sm font-medium">{t('renewals.title')}</p>
          <SubscriptionRenewalsTimeline
            renewals={renewalsQuery.data}
            isLoading={renewalsQuery.isLoading}
            isError={renewalsQuery.isError}
            onRetry={() => renewalsQuery.refetch()}
            subscription={sub}
            servicePrice={
              // SKELETON-0283: Betragsschaetzung nur mit vollstaendigem Join
              // (Betrag UND Waehrung) — sonst "Betrag unbekannt" statt ≈ 0,00
              service && !serviceQuery.isError &&
              typeof service.grossAmount === 'number' && service.currency
                ? { amount: service.grossAmount, currency: service.currency }
                : undefined
            }
          />
        </div>

        {sub.status === 'active' && onTerminate && (
          <div className="flex justify-end pt-3">
            <Button
              variant="destructive"
              size="sm"
              onClick={onTerminate}
              disabled={isTerminating}
            >
              {t('terminate.submit')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
